<template>
  <gv-modal :size="isMobile ? 'sm' : 'lg'" :modal="modal" @close="onClose">
    <template #header>
      <div></div>
    </template>
    <template #default>
      <div v-if="fdr" class="flex flex-col gap-y-6">
        <div class="flex flex-row justify-between bg-grey-25 py-[20px] pl-6 pr-4">
          <div class="flex items-center gap-4">
            <gv-image class="size-10" :src="scheme?.icon" :alt="'fdr' + ' icon'" />
            <div class="flex flex-1 flex-col">
              <gv-type uplifted-variant="subtitle-sm" :text="fdr?.title" breakpoint="none" />
              <div class="text-xs text-type-secondary xs:text-base">
                <gv-type uplifted-variant="body-2" :text="forecast?.fireDanger" />
              </div>
            </div>
          </div>
          <gv-icon class="cursor-pointer" name="close" size="md" @click.stop="onClose" />
        </div>

        <div class="flex flex-col gap-y-4 px-6 pb-6">
          <div>
            <gv-type uplifted-variant="heading" text="Local government areas affected" breakpoint="none" />
            <ul class="ml-2 grow list-inside list-disc text-base text-type-secondary">
              <li v-for="lga of lgas" :key="lga">{{ lga }}</li>
            </ul>
          </div>

          <div v-if="scheme?.entitySubType !== FdrSubType.NoRatingFdrEntity">
            <gv-type uplifted-variant="heading" :text="extraTextHeader[scheme?.entitySubType as FdrSubType]" breakpoint="none" />
            <ul class="ml-2 grow list-inside list-disc text-base text-type-secondary">
              <li>{{ extraText[scheme?.entitySubType as FdrSubType] }}</li>
            </ul>
          </div>

          <div v-if="scheme?.entitySubType === FdrSubType.NoRatingFdrEntity">
            <p class="text-base text-type-secondary" v-html="useSanitiseHtml(noRatingText)"></p>
          </div>

          <div v-if="scheme?.entitySubType !== FdrSubType.NoRatingFdrEntity">
            <gv-type uplifted-variant="heading" text="What to do" breakpoint="none" />
            <ul class="ml-2 grow list-inside list-disc text-base text-type-secondary">
              <li v-for="(item, i) in whatToDoText[scheme?.entitySubType as FdrSubType]" :key="i" v-html="useSanitiseHtml(item)"></li>
            </ul>
          </div>

          <ui-map-popup-footer left-text="Show map" :left-callback="showOnMap" :show-top-border="false" right-text="" />
        </div>
      </div>
    </template>
  </gv-modal>
</template>

<script setup lang="ts">
import { UseModal, useSanitiseHtml } from '@/common';
import gvModal from '@/common/components/gvModal/gvModal.vue';
import { BREAKPOINTS } from '@/constants/index';
import {
  FdrSubType,
  FireDangerRatingEntity,
  HazardType,
  HazardView,
  extraTextHeader,
  extraText,
  whatToDoText,
  noRatingText,
  SelectedHazardViewType,
  useHazardsStore,
} from '@/features/hazards';
import districtLgaMapping from '@/scripts/map-district-to-lga/district-to-lga-mapping.json';
import { useBreakpoints } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed, watch } from 'vue';

const props = defineProps<{
  modal: UseModal;
}>();

const breakpoints = useBreakpoints(BREAKPOINTS);
const isMobile = breakpoints.smaller('xs');

const hazardsStore = useHazardsStore();
const { selectedHazard, hazards } = storeToRefs(hazardsStore);

const fdr = computed<FireDangerRatingEntity | undefined>(() =>
  hazards.value.fireDangerRatings.find((fdr) => fdr.id === selectedHazard.value?.hazardId)
);

const scheme = computed(() => {
  return fdr.value?.pointFeature?.properties?.schemes[hazardsStore.selectedDateIndex];
});

const forecast = computed(() => {
  return fdr.value?.pointFeature?.properties?.forecasts[hazardsStore.selectedDateIndex];
});

const showOnMap = () => {
  const { id } = fdr.value ?? {};

  if (hazardsStore.activeView === HazardView.List) {
    hazardsStore.setActiveView(HazardView.Both);
  }

  onClose();

  if (id) {
    hazardsStore.toggleSelectHazard({
      hazardId: id,
      hazardType: HazardType.FireDangerRating,
      eventId: '',
      viewType: SelectedHazardViewType.Popup,
    });
  }
};

const lgas = computed(() => {
  const district = fdr.value?.title;
  if (!district) return [];

  return districtLgaMapping[district as keyof typeof districtLgaMapping] ?? [];
});

const onClose = () => {
  props.modal.setClosed();
};

watch(
  selectedHazard,
  (newSelectedHazard) => {
    if (
      newSelectedHazard &&
      newSelectedHazard.hazardType === HazardType.FireDangerRating &&
      newSelectedHazard.viewType === SelectedHazardViewType.Modal
    ) {
      props.modal.setOpen();
    } else {
      props.modal.setClosed();
    }
  },
  { immediate: true }
);
</script>
